import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Badge
} from '@mui/material';
import {
  CheckCircle,
  Edit3,
  UserX,
  AlertTriangle,
  Zap,
  Sparkles,
  RefreshCw,
  Clock,
  ShieldAlert,
  Star,
  Trash2
} from 'lucide-react';
import {
  TriageItem,
  getTriageQueue,
  approveTriageDraft,
  amendTriageDraft,
  takeoverTriageDraft,
  discardTriageDraft
} from '../../services/triageService';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';

const SOCKET_URL = API_BASE_URL.replace('/api/v1', '').replace(/\/$/, '');

export const HolyTrinityTriageQueue: React.FC = () => {
  const [queue, setQueue] = useState<TriageItem[]>([]);
  const [channelFilter, setChannelFilter] = useState<'all' | 'whatsapp' | 'instagram' | 'facebook' | 'web'>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [amendedTextMap, setAmendedTextMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await getTriageQueue();
      setQueue(items);
    } catch (err: any) {
      console.error('[HolyTrinity] Failed to fetch triage queue:', err);
      setError('Could not load triage queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();

    // Socket.IO Real-time Connection to Flashing Triage Queue
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      socket.emit('join_ops');
    });

    socket.on('triage:queue_update', (data: { action: string; draft?: TriageItem; id?: string }) => {
      if (data.action === 'new_item' && data.draft) {
        setQueue(prev => [data.draft!, ...prev.filter(i => i._id !== data.draft!._id)]);
      } else if (data.action === 'resolved' && data.id) {
        setQueue(prev => prev.filter(i => i._id !== data.id));
      }
    });

    socket.on('TRIAGE_DRAFT_CREATED', (data: { draft?: TriageItem; id?: string }) => {
      const draft = data.draft;
      if (draft) {
        setQueue(prev => [draft, ...prev.filter(i => i._id !== draft._id)]);
      }
    });

    socket.on('DRAFT_ORDER_CREATED', (data: any) => {
      if (data && data.draftId) {
        const triageItem: TriageItem = {
          _id: data.draftId,
          conversationId: data.draftId,
          waId: data.senderPhone || '',
          customerName: data.customerName || 'Valued Customer',
          isVip: false,
          channel: 'whatsapp',
          rawInboundText: data.rawInput || `WhatsApp Order: ${data.itemsCount || 1} items (${data.currency || 'PKR'} ${((data.totalCents || 0) / 100).toFixed(0)})`,
          triageCategory: 'order_intent',
          triageSeverity: data.clinicalAllergenFlags?.length > 0 ? 'high' : 'medium',
          aiDraftResponse: `Draft Order #${data.draftId.slice(-6)} received (${data.itemsCount} items). Total: ${data.currency} ${((data.totalCents || 0) / 100).toFixed(0)}`,
          status: 'pending',
          llmProvider: data.llmProvider || 'sovereign-local',
          llmLatencyMs: 0,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.createdAt || new Date().toISOString(),
        };
        setQueue(prev => [triageItem, ...prev.filter(i => i._id !== data.draftId)]);
      }
    });

    socket.on('TRIAGE_DRAFT_RESOLVED', (data: { id?: string }) => {
      if (data.id) {
        setQueue(prev => prev.filter(i => i._id !== data.id));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchQueue]);

  // Holy Trinity Action 1: One-Click Approve
  const handleApprove = async (item: TriageItem) => {
    try {
      setActionLoading(item._id);
      await approveTriageDraft(item._id);
      setQueue(prev => prev.filter(i => i._id !== item._id));
    } catch (err: any) {
      if (err.response?.data?.code === 'WINDOW_EXPIRED_24H' || err.response?.data?.error?.includes('WINDOW_EXPIRED_24H')) {
        alert('⚠️ Meta 24-Hour Policy Window has expired. Standard reply dispatch is prohibited per Meta platform policies.');
      } else {
        alert(err.response?.data?.error || 'Failed to approve draft');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Holy Trinity Action 2: Amend & Dispatch
  const handleAmendSubmit = async (item: TriageItem) => {
    const text = amendedTextMap[item._id] || item.aiDraftResponse;
    try {
      setActionLoading(item._id);
      await amendTriageDraft(item._id, text);
      setQueue(prev => prev.filter(i => i._id !== item._id));
      setEditingId(null);
    } catch (err: any) {
      if (err.response?.data?.code === 'WINDOW_EXPIRED_24H' || err.response?.data?.error?.includes('WINDOW_EXPIRED_24H')) {
        alert('⚠️ Meta 24-Hour Policy Window has expired. Standard reply dispatch is prohibited per Meta platform policies.');
      } else {
        alert(err.response?.data?.error || 'Failed to amend draft');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Holy Trinity Action 3: Human Takeover
  const handleTakeover = async (item: TriageItem) => {
    if (!window.confirm('Silence AI and take over live conversation with this customer?')) return;
    try {
      setActionLoading(item._id);
      await takeoverTriageDraft(item._id);
      setQueue(prev => prev.filter(i => i._id !== item._id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to take over conversation');
    } finally {
      setActionLoading(null);
    }
  };

  // Holy Trinity Action 4: Discard / Delete
  const handleDiscard = async (item: TriageItem) => {
    if (!window.confirm('Discard this draft and permanently remove from triage queue?')) return;
    try {
      setActionLoading(item._id);
      await discardTriageDraft(item._id, item.conversationId);
      setQueue(prev => prev.filter(i => i._id !== item._id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to discard triage draft');
    } finally {
      setActionLoading(null);
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'toxic_deescalation': return { bg: '#fee2e2', text: '#b91c1c', border: '#ef4444', label: '🔴 Toxic / Delay Complaint' };
      case 'order_intent': return { bg: '#fef3c7', text: '#b45309', border: '#f59e0b', label: '🟡 Order Intent Awaiting Review' };
      case 'vip_custom_request': return { bg: '#dbeafe', text: '#1d4ed8', border: '#3b82f6', label: '🔵 Custom VIP Artisan Request' };
      case 'recipe_inquiry': return { bg: '#fef3c7', text: '#b45309', border: '#f59e0b', label: 'Proprietary Deflection' };
      case 'discount_negotiation': return { bg: '#e0e7ff', text: '#4338ca', border: '#6366f1', label: 'Zero Discount Boundary' };
      default: return { bg: '#f1f5f9', text: '#475569', border: '#94a3b8', label: 'General Inquiry' };
    }
  };

  const renderChannelBadge = (channel: string) => {
    switch (channel) {
      case 'instagram':
        return (
          <Chip
            label="[IG] Instagram"
            size="small"
            sx={{
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        );
      case 'facebook':
        return (
          <Chip
            label="[FB] Messenger"
            size="small"
            sx={{
              bgcolor: '#0084FF',
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        );
      case 'whatsapp':
        return (
          <Chip
            label="[WA] WhatsApp"
            size="small"
            sx={{
              bgcolor: '#25D366',
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        );
      case 'web':
      default:
        return (
          <Chip
            label="[WEB] Web"
            size="small"
            sx={{
              bgcolor: '#0284c7',
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        );
    }
  };

  const getMetaWindowStatus = (inboundTimestamp?: string, createdAt?: string) => {
    const inboundTime = new Date(inboundTimestamp || createdAt || Date.now()).getTime();
    const elapsedMs = Date.now() - inboundTime;
    const remainingMs = 86400000 - elapsedMs; // 24 Hours
    const isExpired = remainingMs <= 0;
    const hoursLeft = Math.max(0, remainingMs / 3600000);
    return { isExpired, hoursLeft, remainingMs };
  };

  const filteredQueue = channelFilter === 'all'
    ? queue
    : queue.filter(item => item.channel === channelFilter);

  return (
    <Box sx={{ mb: 4 }}>
      <style>
        {`
          @keyframes triagePulse {
            0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
          }
          .flashing-triage-badge {
            animation: triagePulse 1.8s infinite;
          }
        `}
      </style>

      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" fontWeight="bold">
            Shadow Mode Copilot: Flashing Triage Queue
          </Typography>
          {queue.length > 0 && (
            <Chip
              label={`${queue.length} Awaiting Action`}
              color="error"
              size="small"
              className="flashing-triage-badge"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>
        <IconButton onClick={fetchQueue} disabled={loading} size="small">
          <RefreshCw size={18} />
        </IconButton>
      </Box>

      {/* Sprint v6.29.0: Multi-Channel Filter Strip */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={`All Channels (${queue.length})`}
          color={channelFilter === 'all' ? 'primary' : 'default'}
          onClick={() => setChannelFilter('all')}
          variant={channelFilter === 'all' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
        <Chip
          label={`WhatsApp (${queue.filter(i => i.channel === 'whatsapp').length})`}
          color={channelFilter === 'whatsapp' ? 'success' : 'default'}
          onClick={() => setChannelFilter('whatsapp')}
          variant={channelFilter === 'whatsapp' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
        <Chip
          label={`Instagram (${queue.filter(i => i.channel === 'instagram').length})`}
          color={channelFilter === 'instagram' ? 'secondary' : 'default'}
          onClick={() => setChannelFilter('instagram')}
          variant={channelFilter === 'instagram' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
        <Chip
          label={`Facebook (${queue.filter(i => i.channel === 'facebook').length})`}
          color={channelFilter === 'facebook' ? 'primary' : 'default'}
          onClick={() => setChannelFilter('facebook')}
          variant={channelFilter === 'facebook' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
        <Chip
          label={`Web (${queue.filter(i => i.channel === 'web').length})`}
          color={channelFilter === 'web' ? 'info' : 'default'}
          onClick={() => setChannelFilter('web')}
          variant={channelFilter === 'web' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && queue.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : filteredQueue.length === 0 ? (
        <Card sx={{ bgcolor: '#f8fafc', border: '1px dashed #cbd5e1', p: 3, textAlign: 'center' }}>
          <CheckCircle size={36} color="#10b981" style={{ margin: '0 auto 8px auto' }} />
          <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
            {queue.length === 0 ? 'Triage Queue Clear' : `No items in ${channelFilter.toUpperCase()} channel`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI Copilot is monitoring live channels. High-friction or VIP requests will flash here for Holy Trinity intervention.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filteredQueue.map(item => {
            const catInfo = getCategoryColor(item.triageCategory);
            const isAmending = editingId === item._id;
            const isMetaChannel = item.channel === 'instagram' || item.channel === 'facebook';
            const metaWindow = isMetaChannel ? getMetaWindowStatus(item.inboundTimestamp, item.createdAt) : null;

            return (
              <Card
                key={item._id}
                sx={{
                  borderLeft: `6px solid ${item.triageSeverity === 'high' ? '#ef4444' : '#f59e0b'}`,
                  boxShadow: 2,
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  {/* Top Meta Bar */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {renderChannelBadge(item.channel)}
                      <Typography variant="subtitle1" fontWeight="bold">
                        {item.customerName} ({item.externalUserId || item.waId})
                      </Typography>
                      {item.isVip && (
                        <Chip
                          icon={<Star size={12} fill="#fbbf24" color="#d97706" />}
                          label="VIP Customer"
                          size="small"
                          sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 'bold' }}
                        />
                      )}
                      <Chip
                        label={catInfo.label}
                        size="small"
                        sx={{ bgcolor: catInfo.bg, color: catInfo.text, fontWeight: 'bold' }}
                      />
                      {/* Meta 24-Hour Window Badge */}
                      {metaWindow && (
                        metaWindow.isExpired ? (
                          <Chip
                            label="⛔ 24h Window Expired"
                            size="small"
                            sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 'bold' }}
                          />
                        ) : (
                          <Chip
                            label={`⏱️ ${metaWindow.hoursLeft.toFixed(1)}h left`}
                            size="small"
                            sx={{
                              bgcolor: metaWindow.hoursLeft <= 4 ? '#fef3c7' : '#dcfce7',
                              color: metaWindow.hoursLeft <= 4 ? '#b45309' : '#15803d',
                              fontWeight: 'bold'
                            }}
                          />
                        )
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Clock size={12} />
                      {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>

                  {/* 24-Hour Compliance Expiration Alert Banner */}
                  {metaWindow?.isExpired && (
                    <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
                      ⚠️ <strong>Meta 24-Hour Policy Window Expired:</strong> Standard direct message dispatch is disabled per Meta platform policies.
                    </Alert>
                  )}

                  {/* Customer Inbound Message */}
                  <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 1.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      INBOUND MESSAGE:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1e293b', mt: 0.5 }}>
                      "{item.rawInboundText}"
                    </Typography>
                  </Box>

                  {/* AI Generated Draft / Editable Field */}
                  <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', p: 1.5, borderRadius: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#166534', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Sparkles size={14} /> AI DRAFT ({item.llmProvider.toUpperCase()} • {item.llmLatencyMs}ms):
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          if (!isAmending) {
                            setAmendedTextMap(prev => ({ ...prev, [item._id]: item.aiDraftResponse }));
                          }
                          setEditingId(isAmending ? null : item._id);
                        }}
                        startIcon={<Edit3 size={13} />}
                        sx={{ textTransform: 'none', py: 0 }}
                      >
                        {isAmending ? 'Cancel Edit' : 'Edit Draft'}
                      </Button>
                    </Box>

                    {isAmending ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={amendedTextMap[item._id] ?? item.aiDraftResponse}
                        onChange={(e) => setAmendedTextMap(prev => ({ ...prev, [item._id]: e.target.value }))}
                        size="small"
                        sx={{ mt: 1, bgcolor: '#ffffff' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#14532d', whiteSpace: 'pre-wrap' }}>
                        {item.aiDraftResponse}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* HOLY TRINITY UI ACTION CONTROLS */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Action 1: Approve (🟢) */}
                    {!isAmending ? (
                      <Tooltip title={metaWindow?.isExpired ? "Meta 24h reply window has elapsed. Direct message dispatch is blocked." : ""}>
                        <span>
                          <Button
                            variant="contained"
                            color="success"
                            disabled={actionLoading === item._id || Boolean(metaWindow?.isExpired)}
                            onClick={() => handleApprove(item)}
                            startIcon={<CheckCircle size={16} />}
                            sx={{
                              fontWeight: 'bold',
                              px: 2.5,
                              py: 1,
                              borderRadius: 2,
                              bgcolor: metaWindow?.isExpired ? '#9ca3af' : '#10b981',
                              '&:hover': { bgcolor: metaWindow?.isExpired ? '#9ca3af' : '#059669' }
                            }}
                          >
                            Approve (1-Touch)
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={metaWindow?.isExpired ? "Meta 24h reply window has elapsed. Direct message dispatch is blocked." : ""}>
                        <span>
                          <Button
                            variant="contained"
                            color="warning"
                            disabled={actionLoading === item._id || Boolean(metaWindow?.isExpired)}
                            onClick={() => handleAmendSubmit(item)}
                            startIcon={<Zap size={16} />}
                            sx={{ fontWeight: 'bold', px: 2.5, py: 1, borderRadius: 2 }}
                          >
                            Amend & Dispatch
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    {/* Action 2: Amend Trigger (if not in editing mode) */}
                    {!isAmending && (
                      <Button
                        variant="outlined"
                        color="warning"
                        disabled={actionLoading === item._id}
                        onClick={() => {
                          setAmendedTextMap(prev => ({ ...prev, [item._id]: item.aiDraftResponse }));
                          setEditingId(item._id);
                        }}
                        startIcon={<Edit3 size={16} />}
                        sx={{ fontWeight: 'bold', borderRadius: 2 }}
                      >
                        Amend
                      </Button>
                    )}

                    {/* Action 3: Take Over (🔴) */}
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={actionLoading === item._id}
                      onClick={() => handleTakeover(item)}
                      startIcon={<UserX size={16} />}
                      sx={{ fontWeight: 'bold', borderRadius: 2 }}
                    >
                      Take Over
                    </Button>

                    {/* Action 4: Discard / Delete (🗑️) */}
                    <Tooltip title="Discard unapproved draft and exclude from training corpus">
                      <Button
                        variant="outlined"
                        color="inherit"
                        disabled={actionLoading === item._id}
                        onClick={() => handleDiscard(item)}
                        startIcon={<Trash2 size={16} />}
                        sx={{
                          fontWeight: 'bold',
                          borderRadius: 2,
                          color: '#dc2626',
                          borderColor: '#fca5a5',
                          '&:hover': {
                            borderColor: '#dc2626',
                            bgcolor: '#fef2f2'
                          }
                        }}
                      >
                        Discard / Delete
                      </Button>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default HolyTrinityTriageQueue;
